import { httpRequest, httpRequestStatus } from '@/shared/api/client';
import { parseErrorsFromResponse } from '@/shared/api/jsonapi/errors';
import { md5 } from '@/shared/lib/md5';
import type { AuthEnvelope, LoginPayload, RegisterPayload } from './types';
import { baseEndpoint } from '@/shared/lib/baseEndpoint';

const ACCOUNT_BASE = '/api/account';
const ACCOUNT_MGMT_BASE = '/api/account-management';

export interface OtpVerifyPayload {
  Email: string;
  Otp: string;
  FingerPrint?: string;
  UserLang?: string;
}

// /account/* and /refresh-token are custom REST controllers but BE declares
// [Consumes("application/vnd.api+json")] on most POSTs, so we keep the default
// content-type. Logout has no body — we send `{}` to satisfy negotiation.

export async function login(payload: LoginPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/login`, { method: 'POST', body: payload, anonymous: true });
}

export async function register(payload: RegisterPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/register`, { method: 'POST', body: payload, anonymous: true });
}

export async function logout(): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/logout`, { method: 'POST', body: {} });
}

/**
 * Refresh the access token using the long-lived refresh token as Authorization.
 * BE expects `Authorization: Bearer <refreshToken>` (not the access token).
 */
export async function refreshAccessToken(refreshToken: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>('/api/refresh-token', {
    method: 'GET',
    anonymous: true,
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
}

export async function changeTenant(tenantId: string, refreshToken: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`/api/refresh-token/change-tenant?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'GET',
    anonymous: true,
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
}

export async function loginWithOtp(payload: OtpVerifyPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/login-otp`, { method: 'POST', body: payload, anonymous: true });
}

export async function registerWithOtp(payload: OtpVerifyPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/registerOtp`, { method: 'POST', body: payload, anonymous: true });
}

/**
 * Reset/imposta password via link OTP (recovery e link "imposta password" degli
 * inviti Template A). Il model BE è `{ Otp, md5Password, email }`: la password
 * viaggia in MD5 (coerente con setup.useMD5). NB: il vecchio campo `NewPassword`
 * non bindava sul BE → il reset falliva silenziosamente.
 */
export async function resetPasswordWithOtp(email: string, otp: string, newPassword: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/reset-password-otp`, {
    method: 'POST',
    body: { email, Otp: otp, md5Password: md5(newPassword) },
    anonymous: true,
  });
}

/** Shape di GET /api/account/me (Sprint 1 §3): current-user sempre fresco, per il wall termini. */
export interface AccountMe {
  id: string;
  email?: string;
  userName?: string;
  /** Azienda di **appartenenza** dell'utenza. In cross-tenant diverge da `currentTenantId`. */
  tenantId?: string;
  /**
   * L'azienda su cui si sta operando **adesso**, risolta dal server.
   *
   * Diverge da `tenantId` quando la sessione lavora su un'altra società del gruppo, ed è quella su
   * cui il server ha risolto `employeeId`.
   */
  currentTenantId?: string;
  /**
   * L'anagrafica dipendente del chiamante su `currentTenantId`, `null` se non ce l'ha.
   *
   * ⚠️ **Questa è la fonte di «chi sono» come dipendente**, e il campo esiste proprio per non
   * farlo dedurre al client: `ErpEmployee` usa il perimetro dell'albero holding, non l'azienda
   * secca, quindi una lettura per `equals(user.id, …)` restituisce anche le anagrafiche delle
   * altre società del gruppo. Prenderne la prima riga ha già svuotato il timesheet una volta
   * (commento nel BE, 28 agosto 2026). Si legge da qui: vedi `useMyEmployee`.
   */
  employeeId?: string | null;
  /**
   * `true` quando qui non hai un'anagrafica **ma ce l'hai in un'altra azienda**.
   *
   * È una diagnosi, non un permesso: serve a poter dire «sei sull'azienda sbagliata» invece di
   * mostrare un vicolo cieco. Non dice quale azienda.
   */
  hasEmployeeElsewhere?: boolean;
  firstName?: string;
  lastName?: string;
  termsAccepted?: boolean;
  termsAcceptanceDate?: string | null;
  cookieAccepted?: boolean | null;
  superadmin?: boolean;
  /**
   * Gli interruttori di modulo dell'azienda **corrente** (`currentTenantId`), `codice → bool`
   * (BE-CONTRACT-TENANT-MODULES-v1 §4). E' la stessa riga che il cancello del BE legge per
   * rispondere 403 `MODULE_DISABLED`, ed e' leggibile da chiunque: il menu parte da qui
   * (`useContestoAccesso`), non da `tenant-attributes`, che il dipendente base non puo' leggere.
   * Assente finche' il BE non e' deployato con il contratto: chi lo legge deve avere un ripiego.
   */
  modules?: Record<string, boolean>;
  /**
   * Il nome da mostrare dell'azienda su cui si sta lavorando, già risolto (task 2061): il proprio, o
   * quello della capogruppo se l'area «Aspetto» è bloccata, altrimenti la ragione sociale. Cambia col
   * cambio d'azienda.
   */
  tenantDisplayName?: string | null;
  /**
   * Le regole di prenotazione dello smart working per l'azienda corrente, leggibili da chi prenota
   * (task 1899, BE-RESPONSE-TO-FE-IMPOSTAZIONI-SMART-WORKING-PER-CHI-PRENOTA-v1). Prima stavano solo
   * in `tenant-attributes`, che il dipendente non legge. I due numeri arrivano già risolti (default
   * del server compreso); `planningWindow` è `null` senza riga, e il default lo mettiamo noi.
   * In esercizio dalla v1.247.0 (08-10-2026).
   */
  smartWorking?: {
    /** 0 = nessun limite. */
    bookingHorizonDays?: number;
    retroactiveMaxDays?: number;
    planningWindow?: string | null;
    /* Task 1921: se la persona può chiedere giorni da remoto, e da quando. */
    eligible?: boolean;
    eligibleFrom?: string | null;
    eligibilitySource?: 'policy' | 'allowed' | 'denied' | null;
  };
}

export async function getAccountMe(): Promise<AccountMe> {
  const res = await httpRequest<{ success: boolean; data: AccountMe }>(`${ACCOUNT_BASE}/me`, { endpointType: 'plain' });
  return res.data;
}

/**
 * Registra l'accettazione dei termini d'uso per l'utente collegato.
 *
 * ⚠️ **Non passa da `user-profiles`, ed è il punto.** Quella risorsa sta dietro il cancello dei
 * claim, e un utente appena invitato non ne ha nessuno: `SelfServiceEmployee` — l'unico profilo che
 * porta `UserProfile.read/update` — il BE glielo attacca solo quando viene collegato a
 * un'anagrafica dipendente attiva. Fino a quel momento ogni tentativo di accettare tornava
 * `401 CLAIM_MISSING`, e siccome il velo dei termini copre tutta l'area autenticata l'account non
 * era limitato: era **chiuso fuori**. Il ripiego «creo il profilo se manca» era peggio ancora,
 * perché `UserProfile.create` nel catalogo non ce l'ha **nessun ruolo**.
 *
 * L'endpoint sta su `AccountController`, che è esente dal cancello per costruzione — lo stesso
 * motivo per cui `account/me` risponde a chi il velo lo sta guardando. È idempotente e crea il
 * profilo se manca (BE: `AccountController.AcceptTerms`).
 *
 * Ritorna `false` **solo** se l'endpoint non esiste ancora (404: BE non ancora rilasciato), così
 * il chiamante può ripiegare sulla vecchia strada invece di lasciare l'utente davanti a un muro.
 * Ogni altro errore viene lanciato normalmente.
 */
export async function acceptTerms(): Promise<void> {
  const { status, body } = await httpRequestStatus(`${ACCOUNT_BASE}/accept-terms`, {
    method: 'POST',
    endpointType: 'plain',
  });
  /* Il 404 non è più un «ripiega»: l'endpoint è in esercizio dalla v1.211.0, e dall'08-10-2026
     (BE v1.247.0, che crea anche i profili mancanti) la strada vecchia su `user-profiles` è tolta.
     Un 404 qui è un guasto, e si dice come tale. */
  if (status >= 400) throw parseErrorsFromResponse(status, body);
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await httpRequest(`${ACCOUNT_MGMT_BASE}/change-password`, {
    method: 'POST',
    body: { OldPassword: oldPassword, NewPassword: newPassword },
  });
}

/**
 * Cambio password obbligatorio al primo accesso / password scaduta (quando il login → 409).
 * Endpoint [AllowAnonymous]: identifica l'utente via `Username` + `OldPassword` (nessun JWT).
 * In caso di successo il BE esegue internamente il re-login e ritorna l'AuthEnvelope
 * (200 con JWT), quindi NON serve una seconda chiamata di login.
 * old/new vanno passati già in MD5 (coerente con `login`/`changePassword` e `setup.useMD5`).
 * Il device fingerPrint viaggia come header (iniettato dal client) e serve al re-login lato BE.
 */
export async function changePasswordFirstAccess(
  email: string,
  oldPassword: string,
  newPassword: string,
): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_MGMT_BASE}/change-password`, {
    method: 'POST',
    anonymous: true,
    body: { Username: email, OldPassword: oldPassword, NewPassword: newPassword, ConfirmPassword: newPassword },
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/reset-password`, {
    method: 'POST',
    body: { Email: email },
    anonymous: true,
  });
}

// ─── Recupero password con codice a 6 cifre (BE: account/password-reset-code) ────────────────
// Flusso DISTINTO da `requestPasswordReset`/`resetPasswordWithOtp` qui sopra, che manda per email un
// link con un token GUID: quello resta invariato lato BE e continua a funzionare. Qui l'utente riceve
// un codice numerico, lo digita, e solo dopo sceglie la password. Endpoint "plain" (application/json),
// non JSON:API.

/**
 * Passo 1 — invia il codice via email. Risponde 204 anche se l'email non è registrata
 * (il BE non rivela quali indirizzi esistono), quindi il successo NON significa "utente trovato".
 * L'header `baseEndpoint` serve al BE per i segnaposto del template email.
 */
export async function requestPasswordResetCode(email: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/password-reset-code`, {
    method: 'POST',
    body: { email },
    anonymous: true,
    endpointType: 'plain',
    headers: { baseEndpoint: baseEndpoint() },
  });
}

export interface PasswordResetTicket {
  /** Segreto monouso che autorizza il cambio password: da passare a `confirmPasswordResetCode`. */
  ticket: string;
  expiresAt: string | null;
}

/**
 * Passo 2 — valida il codice e ottiene il ticket. In errore il BE manda
 * `code` = CODE_INVALID | CODE_EXPIRED | TOO_MANY_ATTEMPTS con un messaggio già in italiano.
 */
export async function verifyPasswordResetCode(email: string, code: string): Promise<PasswordResetTicket> {
  return httpRequest<PasswordResetTicket>(`${ACCOUNT_BASE}/password-reset-code/verify`, {
    method: 'POST',
    body: { email, code },
    anonymous: true,
    endpointType: 'plain',
  });
}

/** Passo 3 — imposta la nuova password consumando il ticket. La password viaggia in MD5. */
export async function confirmPasswordResetCode(email: string, ticket: string, newPassword: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/password-reset-code/confirm`, {
    method: 'POST',
    body: { email, ticket, md5Password: md5(newPassword) },
    anonymous: true,
    endpointType: 'plain',
    headers: { baseEndpoint: baseEndpoint() },
  });
}

export async function confirmRegistration(token: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/confirmRegistration`, {
    method: 'POST',
    body: { Token: token },
    anonymous: true,
  });
}

// ─── Google login "JWT diretto" (BE v1.9+) ──────────────────────────────────
// Scambia l'id_token di Google Identity Services con il JWT della piattaforma.
// 404 = utente non registrato (va indirizzato alla registrazione).

export async function googleLoginJwt(idToken: string, fingerPrint: string, userLang?: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/google-login-jwt`, {
    method: 'POST',
    body: { idToken, fingerPrint, userLang },
    anonymous: true,
  });
}

// ─── Passkey / WebAuthn (BE v1.9+, modello stateless) ───────────────────────
// Le `begin` ritornano optionsJson (JSON WebAuthn standard) + state opaco da
// rimandare alla `finish`. login/* sono anonimi e rate-limited; register/* bearer.

export interface PasskeyBeginResponse {
  optionsJson: string;
  state: string;
}

export async function passkeyRegisterBegin(displayName?: string): Promise<PasskeyBeginResponse> {
  return httpRequest<PasskeyBeginResponse>('/api/passkey/register/begin', {
    method: 'POST',
    body: { displayName },
  });
}

export async function passkeyRegisterFinish(credentialJson: string, state: string, name?: string): Promise<void> {
  await httpRequest('/api/passkey/register/finish', {
    method: 'POST',
    body: { credentialJson, state, name },
  });
}

export async function passkeyLoginBegin(email?: string): Promise<PasskeyBeginResponse> {
  return httpRequest<PasskeyBeginResponse>('/api/passkey/login/begin', {
    method: 'POST',
    body: email ? { email } : {},
    anonymous: true,
  });
}

export async function passkeyLoginFinish(credentialJson: string, state: string, fingerPrint: string, userLang?: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>('/api/passkey/login/finish', {
    method: 'POST',
    body: { credentialJson, state, fingerPrint, userLang },
    anonymous: true,
  });
}

/** Strip "Bearer " prefix returned by BE. */
export function stripBearerPrefix(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.startsWith('Bearer ') ? raw.slice(7) : raw;
}
